import { GraduationCap } from 'lucide-react';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@design-system';
import type { EmailTestResult } from '../api/api';
import { useAddExample } from '../hooks/use-add-example';

/**
 * When the wrong reply, or none, was picked: add the email to the reply that should answer it, as
 * an example, and try the email again with that reply's draft.
 */
export function TeachReply(props: { result: EmailTestResult; email: string; tryAgainWith: (entryId: string) => void }) {
  const teach = useAddExample(props);

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white px-5 py-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">Teach a reply this email</h2>
        <p className="text-[13px] text-zinc-600">
          If the wrong reply was picked, or none, add this email to the reply that should answer it. The assistant
          recognises a reply by its examples. It’s saved as a draft of that reply, and the email is tried again with it.
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="teach-reply">Reply that should answer it</Label>
        <Select
          value={teach.entryId}
          onValueChange={teach.setEntryId}
        >
          <SelectTrigger
            id="teach-reply"
            className="h-11 bg-white"
          >
            <SelectValue placeholder="Choose a reply" />
          </SelectTrigger>
          <SelectContent>
            {teach.groups.map(([topic, replies]) => (
              <SelectGroup key={topic}>
                <SelectLabel>{topic}</SelectLabel>
                {replies.map(reply => (
                  <SelectItem
                    key={reply.entryId}
                    value={reply.entryId}
                  >
                    {reply.title}
                    {reply.status === 'live' ? '' : ' (draft)'}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        {teach.chosen && (
          <span className="text-[12px] text-zinc-500">
            It has {teach.chosen.exampleCount} {teach.chosen.exampleCount === 1 ? 'example' : 'examples'} now.
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="teach-example">The example</Label>
        <Textarea
          id="teach-example"
          rows={3}
          value={teach.example}
          onChange={event => teach.setExample(event.target.value)}
        />
        <span className="text-[12px] text-zinc-500">
          Examples are kept with the reply, so use a made-up client name, like Alex Chen. A client name the assistant
          recognised has been swapped for you.
        </span>
      </div>
      <Button
        variant="outline"
        className="h-11 self-start"
        disabled={!teach.canAdd}
        onClick={teach.addIt}
      >
        <GraduationCap aria-hidden /> {teach.adding ? 'Adding…' : 'Add as an example and try again'}
      </Button>
    </section>
  );
}
